/**
 * The Rx builder's in-progress work, parked LOCALLY.
 *
 * Clicking "Add diagnosis" (etc.) takes the doctor off this screen to the
 * patient record, and the builder holds a lot that lives nowhere else yet:
 * ticked rows, edited patient details, custom instructions, referrals, the
 * follow-up. Losing that on a two-click detour is not acceptable.
 *
 * DELIBERATELY NOT a server draft (Nittin, 2026-07-27): stepping out to add a
 * diagnosis should not mint a prescription record for a patient whose Rx may
 * never be written. Saving a draft stays an explicit act.
 *
 * sessionStorage, not localStorage, and that is the safety-relevant choice —
 * this is a per-tab, per-sitting scratchpad. localStorage would let a
 * half-finished prescription from days ago resurrect itself into a clinical
 * document on a shared clinic machine.
 */

const KEY = (appointmentId) => `zy.RX_SCRATCH.${appointmentId}`;

/** The builder state worth carrying across the detour — and only that. */
const FIELDS = [
  "fields",
  "picks",
  // The rows that were ticked FOR the doctor on this visit, and each row's stamp
  // when it was — see mergePicks.
  "autoPicks",
  "autoStamps",
  // Defaults the doctor took OFF, with the row's stamp at the time, and the day
  // that was decided — see declinedDefaults.
  "declined",
  "declinedOn",
  // bridge only: the prescribing doctor chosen from the care team, so a detour
  // doesn't reset who signs — and whose rows count as this consultation's.
  // phr's prescriber is the signed-in doctor and never parks one.
  "doctorId",
  "customInstructions",
  // The instruction boxes the doctor typed but has not added yet — text they
  // have written, so a "+ Add …" detour must not destroy it, and Publish's
  // "not added yet" refusal must still fire after one (CustomInstructionCard).
  "customInstructionDrafts",
  "referrals",
  "followUp",
  // Not part of `picks` — the advised-investigations card builds its own rows,
  // so leaving it out here would silently empty that card on the detour.
  "investigations",
];

/**
 * The patient-details values the SERVER owns, which the scratch must never
 * carry back.
 *
 * `fields` holds the whole Patient-details card, and NONE of it is editable
 * there now — name, age, gender and phone come from the record, and weight,
 * height, heightUom and the weigh-in date come from the Anthropometric section.
 * Parking them meant the scratch replayed a STALE weight over the fresh
 * reading on every re-entry: applyRestored copies every non-empty parked key
 * and runs AFTER the prefill has landed, so a weight updated in Anthropometric
 * data never appeared in Create Rx again for the rest of the session. *
 * `fullName` joined the list once the name became read-only in the card and
 * editable from the header's Patient-details dialog instead: a parked name
 * could only ever be a stale copy of the record's, and a rename made on the
 * patient record during a "+ Add …" detour was being overwritten by it on the
 * way back — the prescription then printed a name the record no longer had.
 *
 * Stripped on the way IN and on the way OUT — out as well as in, because a
 * session that started before this change still has the old shape parked.
 */
const SERVER_OWNED = ["fullName", "age", "gender", "phone", "weight", "height", "heightUom", "dateOfWeightRecorded"];
const editableOnly = (fields) => {
  if (!fields) return fields;
  return Object.keys(fields).reduce((o, k) => {
    if (SERVER_OWNED.indexOf(k) === -1) o[k] = fields[k];
    return o;
  }, {});
};

/** Is there anything here worth restoring? An untouched builder isn't. */
const worthKeeping = (state) => {
  if (!state) return false;
  const picked = state.picks || {};
  if (Object.keys(picked).some((k) => (picked[k] || []).length)) return true;
  /* A default tick the doctor took OFF is work too: without it parked, the
     next entry re-derives the defaults and ticks the row straight back. */
  const declined = state.declined || {};
  if (Object.keys(declined).some((k) => Object.keys(declined[k] || {}).length)) return true;
  /* bridge: a prescriber the operator CHOSE (the host only passes one then). */
  if (state.doctorId != null) return true;
  if ((state.customInstructions || []).length) return true;
  if ((state.customInstructionDrafts || []).length) return true;
  if ((state.referrals || []).length) return true;
  if ((state.investigations || []).length) return true;
  if (state.followUp && state.followUp.scheduled) return true;
  /* Only the editable half counts. Judging the whole card made an UNTOUCHED
     builder "worth keeping" the moment the prefill populated a read-only
     weight, which is what parked the stale values in the first place. */
  const f = editableOnly(state.fields || {});
  return Object.keys(f).some((k) => f[k] !== "" && f[k] != null);
};

/**
 * Park the builder on the way out — or, when nothing in it is worth keeping,
 * REMOVE what an earlier detour parked.
 *
 * Returning without a write is not neutral: the previous scratch stays in
 * sessionStorage and is restored on the next visit. With every Patient-details
 * field server-owned, a builder the doctor has emptied (a restored tick
 * unticked, a restored investigation removed) is exactly that case — and the
 * row they took off would come back, and be published. The owner calls this
 * only once its restore has landed (see ReviewPrescription), so an empty state
 * here is the doctor's, not a builder still loading.
 */
export const saveScratch = (appointmentId, state, owner) => {
  if (!appointmentId) return;
  if (!worthKeeping(state)) {
    clearScratch(appointmentId);
    return;
  }
  try {
    const slim = FIELDS.reduce((o, k) => {
      o[k] = k === "fields" ? editableOnly(state[k]) : state[k];
      return o;
    }, {});
    slim.declinedOn = today();
    // Which builder parked it — so a publish that finishes after its screen was
    // left clears only its own copy, never a later visit's (ReviewPrescription).
    if (owner) slim.owner = owner;
    window.sessionStorage.setItem(KEY(appointmentId), JSON.stringify(slim));
  } catch (e) {
    /* Private mode, or quota. The detour costs the doctor this builder — but
       leaving the PREVIOUS park in place costs more: readScratch would replay
       an older sitting's rows onto this prescription, which is how a removed
       investigation or instruction comes back and gets published. */
    clearScratch(appointmentId);
  }
};

export const readScratch = (appointmentId) => {
  if (!appointmentId) return null;
  try {
    const raw = window.sessionStorage.getItem(KEY(appointmentId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.fields) parsed.fields = editableOnly(parsed.fields);
    return parsed;
  } catch (e) {
    return null;
  }
};

export const clearScratch = (appointmentId) => {
  try {
    window.sessionStorage.removeItem(KEY(appointmentId));
  } catch (e) {
    /* nothing to do — a stale scratch is dropped on the next tab close */
  }
};

/** The local calendar day — a decline is a decision about THIS consultation. */
const today = () => new Date().toDateString();

const has = (obj, key) => Boolean(obj) && Object.prototype.hasOwnProperty.call(obj, key);

/**
 * The defaults the doctor has taken OFF — `{ [card]: { [rowId]: stamp } }` —
 * carried from one visit to the next.
 *
 * A decline is remembered for as long as the DOCTOR leaves the row alone: the
 * stamp is rxMappers' `myStamp`, which only their own saves move, so a row they
 * unticked and then went and EDITED comes back ticked — the edit is the newer
 * decision — while a colleague's save or a mark-incorrect does not undo it.
 * It is carried forward rather than re-derived from each visit's defaults, or
 * one visit where the row was not a default (its card failed to load, say)
 * would forget it, and the next would tick it straight back onto a prescription
 * the doctor had taken it off.
 *
 * `previous` is the parked set (see restoredDeclined), `autoPicks` this visit's
 * defaults, `picks` what is ticked now; re-ticking a row drops its decline.
 */
export const declinedDefaults = (previous, autoPicks, picks, stampOf) => {
  const out = {};
  const keys = Object.keys(previous || {}).concat(
    Object.keys(autoPicks || {}).filter((k) => !has(previous, k))
  );
  keys.forEach((k) => {
    const ticked = (picks && picks[k]) || [];
    const d = { ...((previous && previous[k]) || {}) };
    ((autoPicks && autoPicks[k]) || []).forEach((id) => {
      if (ticked.indexOf(id) === -1) d[id] = String(stampOf(k, id) || "");
    });
    ticked.forEach((id) => {
      delete d[id];
    });
    if (Object.keys(d).length) out[k] = d;
  });
  return out;
};

/**
 * The parked declines, or null. bridge keys its scratch on the patient alone, so
 * a tab left open overnight would carry yesterday's "not this one" into today's
 * consultation: by default only TODAY's are returned. phr's scratch is already
 * one consultation's (keyed on the appointment), whose rows stay defaults under
 * the appointment date past midnight — it passes `perConsultation` and keeps them.
 */
export const restoredDeclined = (scratch, perConsultation) =>
  scratch && scratch.declined && (perConsultation || scratch.declinedOn === today())
    ? scratch.declined
    : null;

/** `{ [card]: { [rowId]: stamp } }` for the given ticks — parked beside autoPicks. */
export const stampsFor = (picks, stampOf) =>
  Object.keys(picks || {}).reduce((out, k) => {
    out[k] = (picks[k] || []).reduce((m, id) => {
      m[id] = String(stampOf(k, id) || "");
      return m;
    }, {});
    return out;
  }, {});

/**
 * The ticks to start a returning visit with: the parked ticks, reconciled with
 * this visit's defaults (`fresh`).
 *
 *  - KEPT: every parked tick — the union is what makes the detour safe to take —
 *    EXCEPT one that was only ever a default (`prev.autoPicks`), is not one any
 *    more, AND whose row changed since (`prev.autoStamps` vs `prev.stampOf`). A
 *    medicine this doctor added, and a colleague then re-dosed during the detour,
 *    is no longer theirs to prescribe; keeping the old tick would print the
 *    colleague's regimen. A row that stopped being a default only because the
 *    clock passed midnight has not changed, and keeps its tick.
 *  - ADDED: every default not already kept — what the detour just recorded has
 *    to come back ticked — EXCEPT one the doctor declined (`prev.declined`) and
 *    has not themselves changed since (`prev.declineStampOf`).
 *
 * `prev` omitted, or a scratch parked before these fields existed, merges as a
 * plain union.
 */
export const mergePicks = (restored, fresh, prev) => {
  const was = restored || {};
  const wasAuto = (prev && prev.autoPicks) || {};
  const autoStamps = (prev && prev.autoStamps) || {};
  const declined = (prev && prev.declined) || {};
  const stampOf = (prev && prev.stampOf) || (() => "");
  const declineStampOf = (prev && prev.declineStampOf) || stampOf;
  const out = {};
  const keys = Object.keys(fresh || {}).concat(Object.keys(was).filter((k) => !has(fresh, k)));
  keys.forEach((k) => {
    const now = (fresh && fresh[k]) || [];
    const auto = wasAuto[k] || [];
    const parked = autoStamps[k] || {};
    const kept = (was[k] || []).filter(
      (id) =>
        auto.indexOf(id) === -1 ||
        now.indexOf(id) !== -1 ||
        (has(parked, id) && parked[id] === String(stampOf(k, id) || ""))
    );
    const d = declined[k] || {};
    const added = now.filter(
      (id) => kept.indexOf(id) === -1 && !(has(d, id) && d[id] === String(declineStampOf(k, id) || ""))
    );
    out[k] = kept.concat(added);
  });
  return out;
};
